import { ClassResourcesComponent } from '../../components/class-resources/class-resources.component';
import { Pair } from '../pair';
import { AdditionalInformationData } from '../wizard-plugin-models/additional-information-data';
import { BasePluginData } from '../wizard-plugin-models/base-plugin-data';
import { BillingDetailsData } from '../wizard-plugin-models/billing-details-data';
import { RequisitionData } from '../wizard-plugin-models/requisition-data';
import { TrainingSchedulesData } from '../wizard-plugin-models/training-schedules-data';
import { ContactEntity } from './contact-entity';
import { CourseEntity } from './course';
import { CustomerEntity } from './customer';
import { EntityBase } from './entity-base';
import { TrainerEntity } from './trainer';

/**
 * Represents a training request document.
 */
export class Requisition extends EntityBase {
  /**
   * Document identifier.
   * @type(GUid)
   * @default ""
   * @example "6811d1a920529ad393ec323e"
  */
  docId: string = '';

  /**
   * Document number for maintaing requisition no. will be auto created via some logic
   * @type(string)
   * @default ""
   * @example "Req/2025-05-19/1"
   * */
  docNo: string = '';

  /**
   * Date of the document.
   * @type(datetime)
   * @default ""
   * @example 2025-05-01
   *  */
  docDate: Date = new Date();

  /**
   * Customer related details.
   * @type (CustomerDetails)
   *
   * */
  customerDetails: CustomerDetails = new CustomerDetails();

  /** Billing related details. */
  billingDetails: BillingDetails = new BillingDetails();

  /**
   * Any special requirements.
   *
   *  */
  specialRequirements: string = '';

  /** Final comments on the request. */
  finalComments: string = '';

  /** Current status of the request. */
  status: string = '';

  /** List of training TrainingSchedulesData. */
  trainingSchedules: TrainingSchedule[] = [];

  static fromPluginData(finalData: { [key: string]: BasePluginData }): Requisition {
    const req = new Requisition();

    // RequisitionData
    const rd = (finalData['RequisitionData'] as RequisitionData);
    req.docId = rd.docId;
    req.docNo = rd.docNo;
    req.docDate = new Date(rd.docDate);
    req.status = rd.status;

    // CustomerDetails
    const cd = rd.customerDetails;
    req.customerDetails.id = cd.id;
    req.customerDetails.name = cd.name;
    req.customerDetails.isActive = cd.isActive;
    req.customerDetails.createdBy = cd.createdBy;
    req.customerDetails.createdOn = cd.createdOn ? new Date(cd.createdOn) : undefined;
    req.customerDetails.updatedBy = cd.updatedBy;
    req.customerDetails.updatedOn = cd.updatedOn ? new Date(cd.updatedOn) : undefined;
    req.customerDetails.address = cd.address;
    req.customerDetails.website = cd.website;
    req.customerDetails.gstNumber = cd.gstNumber;
    req.customerDetails.contactPerson = Object.assign(new ContactEntity(), cd.contactPerson);
    req.customerDetails.customerType = cd.customerType;

    // BillingDetailsData
    const bd = (finalData['BillingDetailsData'] as BillingDetailsData).billingDetails;
    req.billingDetails.billingAddress = bd.billingAddress;
    req.billingDetails.gstNo = bd.gstNo;
    req.billingDetails.paymentTerms = bd.paymentTerms;
    req.billingDetails.estimateAmount = bd.estimateAmount;

    // TrainingSchedulesData
    const tsd = (finalData['TrainingSchedulesData'] as TrainingSchedulesData);

    req.trainingSchedules = tsd.TrainingSchedulesData[0].dateRanges.map((range: { startDate: string | number | Date; endDate: string | number | Date; }, idx: number) => {
      const ts = new TrainingSchedule();
      ts.reqId = req.docId;
      ts.srNo = idx + 1;
      ts.title = tsd.TrainingSchedulesData[0].title;
      ts.startDate = new Date(range.startDate);
      ts.endDate = new Date(range.endDate);
      // ts.startTime = tsd.activeStart + ':00';
      // ts.endTime = tsd.activeEnd + ':00';
      ts.offDates = [];
      ts.participants = tsd.TrainingSchedulesData[0].participants.map((email: any) => ({ email, redemptionKey: null }));
      ts.trainers = tsd.TrainingSchedulesData[0].trainers;
      ts.resourceTemplateIds = [];
      ts.course = tsd.TrainingSchedulesData[0].course;

      // Deletion policy
      ts.resourcesManagementPolicies.deletion.deletionType = tsd.TrainingSchedulesData[0].removalOption;
      ts.resourcesManagementPolicies.deletion.deletionDate = new Date();
      ts.resourcesManagementPolicies.deletion.deletionTime = '';

      return ts;
    });

    // AdditionalInformationData
    const ad = (finalData['AdditionalInformationData'] as AdditionalInformationData);
    req.specialRequirements = ad.specialRequirements;
    req.finalComments = ad.finalComments;

    return req;
  }
}

/**
 * Details about the customer.
 */
export class CustomerDetails extends CustomerEntity {
  /** Type/category of the customer. */
  customerType: string;

  constructor() {
    super();
    this.customerType = 'Corporate';
  }
}

/**
 * Billing information.
 */
export class BillingDetails {
  /** Billing address. */
  billingAddress: string;

  /** GST number. */
  gstNo: string;

  /** Payment terms. */
  paymentTerms: string;

  /** Estimated amount for billing. */
  estimateAmount: number;

  constructor() {
    this.billingAddress = '';
    this.gstNo = '';
    this.paymentTerms = '';
    this.estimateAmount = 0;
  }
}

/**
 * Schedule for training sessions.
 */
export class TrainingSchedule extends EntityBase {
  /** Requisition identifier to indicate which requisition this schedule is a part of. */
  reqId!: string;

  /** Serial number of the schedule entry. */
  srNo: number;

  /** Title of the session. */
  title: string;

  /** Start date of the session. */
  startDate: Date;

  /** End date of the session. */
  endDate: Date;

  /** Start time of the session. */
  startTime: string;

  /** End time of the session. */
  endTime: string;

  /** Dates when sessions are off. */
  offDates: Date[];

  /** Email addresses of participants. */
  // participantsEmail: string[];
  participants: any[] = [];


  /** Resource template identifiers. */
  resourceTemplateIds: string[];

  course: CourseEntity | undefined;
  /** Email addresses for trainers. */
  trainers: Pair[];


  /** Policies for resource management. */
  resourcesManagementPolicies: ResourcesManagementPolicies;

  constructor() {
    super();
    this.srNo = 0;
    this.title = '';
    this.startDate = new Date();
    this.endDate = new Date();
    this.startTime = '';
    this.endTime = '';
    this.offDates = [];
    this.participants = [];
    this.resourceTemplateIds = [];
    this.trainers = [];
    this.resourcesManagementPolicies = new ResourcesManagementPolicies();
  }
}

/**
 * Resource management policies container.
 */
export class ResourcesManagementPolicies {
  /** Deletion policy settings. */
  deletion: DeletionPolicy;

  constructor() {
    this.deletion = new DeletionPolicy();
  }
}

/**
 * Settings for deletion policy.
 */
export class DeletionPolicy {
  /** Type of deletion (e.g., Scheduled). */
  deletionType: string;

  /** Date for scheduled deletion. */
  deletionDate: Date;

  /** Time for scheduled deletion. */
  deletionTime: string;

  constructor() {
    this.deletionType = '';
    this.deletionDate = new Date();
    this.deletionTime = '';
  }
}


export class RequisitionMovement {
  id?: string;
  requisitionId?: string;
  forwardFromTeamId?: string;
  forwardFromUserId?: string;
  forwardToTeamId?: string;
  forwardToUserId?: string;
  forwardFromTeam?: string;
  forwardFromUser?: string;
  forwardToTeam?: string;
  forwardToUser?: string;
  purpose?: string;
  purposeCode?: string;
  forwardedOn?: Date;
  remarks?: string;
  srNo?: number;
  isRead?: boolean;
  readDate?: Date | null;
  isActive?: boolean;
  isPurposeDone?: boolean;
}

